import {ApolloProvider, useQuery} from '@apollo/client';
import * as React from 'react';
import {useMemo} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router';

import {StudentBoot, readBoot} from './boot';
import {AccountScreens} from './screens';
import {PostDetailScreen} from './screens/PostDetailScreen';
import {WodDetailScreen} from './screens/WodDetailScreen';
import {UnitDetailScreen} from './screens/UnitDetailScreen';
import {StudentScreen} from './StudentScreen';
import {StudentTabBar} from './StudentTabBar';
import {buildStudentClient} from './studentClient';
import {QUERY_STUDENT_ACCOUNT} from './studentQueries';
import {StudentSession} from './studentSession';
import {NavigationTab, buildRoutes, buildTabs} from './studentTabs';
import {AccountColors, buildTheme} from './studentTheme';

import './studentApp.css';

interface AccountData {
  account?: {
    name?: string | null;
    code?: string | null;
    avatar?: {uri?: string | null} | null;
    app?: {
      settings?: {
        colors?: AccountColors | null;
        navigationTabs?: NavigationTab[] | null;
        screens?: AccountScreens | null;
      } | null;
    } | null;
  } | null;
}

const StudentShell: React.FC<{boot: StudentBoot}> = ({boot}) => {
  const {data, loading, error, refetch} = useQuery<AccountData>(QUERY_STUDENT_ACCOUNT);

  const settings = data?.account?.app?.settings;
  const theme = useMemo(() => buildTheme(settings?.colors), [settings?.colors]);
  const tabs = useMemo(() => buildTabs(settings?.navigationTabs), [settings?.navigationTabs]);

  // Enquanto a conta nao chega, so o fundo — piscar a barra com as abas padrao
  // e depois trocar pelas da conta e pior que esperar.
  if (loading && !data) {
    return <div className="student-app student-app--loading" style={{backgroundColor: theme.background}} />;
  }

  // Sem a conta, buildTabs devolveria as abas padrao — o aluno veria uma
  // navegacao que nao e a da conta dele e acharia que o app perdeu telas.
  // Falhar visivelmente, com como tentar de novo, e melhor que enganar.
  if (error && !data) {
    return (
      <div className="student-app student-app--erro" style={{backgroundColor: theme.background, color: theme.text}}>
        <p>Não foi possível carregar {boot.accountName}.</p>
        <button type="button" className="student-app__retry" onClick={() => refetch()}>
          Tentar de novo
        </button>
      </div>
    );
  }

  // tabs[0].screen ja e a rota: buildTabs substitui na leitura do servidor.
  const inicial = tabs[0].screen;

  // Rota para toda tela conhecida, e nao so para as abas visiveis: o menu leva
  // a telas que a conta tirou da barra (a blacksheep, por exemplo, tem
  // Recordes e Favoritos so pelo menu). Sem isso o item cairia no redirect.
  // O app-mobile faz o mesmo, registrando as escondidas com href null.
  const rotas = buildRoutes(tabs);

  return (
    <div className="student-app" style={{backgroundColor: theme.background, color: theme.text}}>
      <main className="student-app__content">
        <Routes>
          <Route path="/" element={<Navigate to={`/${inicial}`} replace />} />
          {rotas.map((tab) => (
            <Route
              key={tab.screen}
              path={`/${tab.screen}/*`}
              element={<StudentScreen tab={tab} theme={theme} screens={settings?.screens} />}
            />
          ))}
          {/* Detalhe fora das abas: Favoritos e Completados apontam para um
              treino ou uma aula mesmo quando a conta tirou a aba deles da
              navegacao. */}
          <Route path="/treino/:wodId" element={<WodDetailScreen theme={theme} />} />
          <Route path="/aula/:unitId" element={<UnitDetailScreen theme={theme} />} />
          <Route path="/post/:postId" element={<PostDetailScreen theme={theme} />} />

          <Route path="*" element={<Navigate to={`/${inicial}`} replace />} />
        </Routes>
      </main>

      <StudentTabBar tabs={tabs} theme={theme} />
    </div>
  );
};

const StudentApp: React.FC = () => {
  const boot = readBoot();

  const client = useMemo(() => {
    if (!boot) return null;

    return buildStudentClient(boot, new StudentSession(boot));
  }, [boot]);

  // Sem payload de boot a pagina nao foi renderizada pelo controller do app —
  // falhar visivelmente e melhor que uma tela branca sem explicacao.
  if (!boot || !client) {
    return <div className="student-app__fatal">Não foi possível carregar o aplicativo.</div>;
  }

  return (
    <ApolloProvider client={client}>
      <BrowserRouter basename={boot.basePath}>
        <StudentShell boot={boot} />
      </BrowserRouter>
    </ApolloProvider>
  );
};

export default StudentApp;
